import { useMemo } from "react";
import CategoryCardView from "./view";
import CategoryCardEdit from "./edit";
import ModalWrapper from "../../../../components/modal/modal";
import { useTranslation } from "react-i18next";

const CategoryCard = ({
  category,
  isOpen,
  close,
  editMode,
  createCategory,
  updateCategory,
  deleteCategory,
  toEditMode,
  form,
}) => {
  const { t } = useTranslation();

  const create = () => {
    const { name, description } = form.getFieldsValue();

    if (!category.id) {
      createCategory({
        name,
        description,
      });
    } else {
      updateCategory(category.id, {
        ...category,
        name,
        description,
      });
    }
  };

  const associationGroups = useMemo(() => {
    if (!category) return [];

    if (category.associations) {
      const src = category.associations;

      if (Array.isArray(src)) {
        return src
          .filter(Boolean)
          .map((g) => ({
            key: g.key || g.title,
            title: g.title,
            count: Number.isInteger(g.count) ? g.count : undefined,
            items: Array.isArray(g.items)
              ? g.items.filter(Boolean).map((it) => ({
                  id: it.id,
                  label: it.label,
                  href: it.href,
                  icon: it.icon,
                  onClick: it.onClick,
                }))
              : [],
          }))
          .filter((g) => g.title);
      }

      if (typeof src === "object") {
        return Object.entries(src)
          .map(([key, g]) => ({
            key,
            title: g?.title || key,
            count: Number.isInteger(g?.count) ? g.count : undefined,
            items: Array.isArray(g?.items)
              ? g.items
                  .filter(Boolean)
                  .map((it) => ({
                    id: it.id,
                    label: it.label,
                    href: it.href,
                    icon: it.icon,
                    onClick: it.onClick,
                  }))
              : [],
          }))
          .filter((g) => g.title);
      }
    }

    const normalizeItems = (arr = []) =>
      (arr || []).filter(Boolean).map((item) => ({
        id: item?.id,
        label:
          item?.label ||
          item?.title ||
          item?.name ||
          item?.value ||
          item?.displayId ||
          "—",
        href: item?.href,
        icon: item?.icon,
      }));

    const groups = [
      {
        key: "tasks",
        title: "Tasks",
        items: normalizeItems(category?.tasks),
      },
      {
        key: "pages",
        title: "Pages",
        items: normalizeItems(category?.pages),
      },
      {
        key: "facts",
        title: "Facts",
        items: normalizeItems(category?.facts),
      },
      {
        key: "scenarios",
        title: "Scenarios",
        items: normalizeItems(category?.scenarios),
      },
      {
        key: "plans",
        title: "Plans",
        items: normalizeItems(category?.plans),
      },
    ]
      .map((g) => ({
        ...g,
        count: g.items.length,
      }))
      .filter((g) => g.count > 0);

    return groups;
  }, [category]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      title={category.id ? "Category" : "Create category"}
      onOk={create}
      onCancel={close}
      okText={t(
        category.id ? "categories.modal.update" : "categories.modal.create"
      )}
      okButtonProps={{
        "data-testid": "category-save",
        size: "large",
        style: { display: editMode ? "" : "none" },
      }}
      cancelButtonProps={{
        size: "large",
        style: {
          backgroundColor: "rgba(108, 108, 137, 0.08)",
          display: editMode ? "" : "none",
        },
      }}
    >
      {editMode ? (
        <CategoryCardEdit
          category={category}
          createCategory={createCategory}
          close={close}
          updateCategory={updateCategory}
          deleteCategory={deleteCategory}
          form={form}
        />
      ) : (
        <CategoryCardView
          category={category}
          close={close}
          toEditMode={toEditMode}
          deleteCategory={deleteCategory}
          form={form}
          associationGroups={associationGroups}
        />
      )}
    </ModalWrapper>
  );
};

export default CategoryCard;